/**
 * SwitchableInput Field Component
 *
 * A dynamic form field that combines a PillSwitch for type selection
 * and an input field for value entry.
 *
 * DATABASE FIELDS (two separate database columns):\n * - key (e.g., "performance"): stores the numeric/text value
 * - typeField (e.g., "performancetype"): stores the selected type from switch
 *
 * SAVE BEHAVIOR:
 * - Both fields trigger save on change (handleChange + handleBlur)
 * - When type changes, value is cleared and type is saved in background (.then)
 *
 * Supported inputType values:
 * - "dynamic": Switches between PercentageInput / FormattedCurrencyInput based on switch
 * - "percentage": Always renders PercentageInput
 * - "currency": Always renders FormattedCurrencyInput
 * - "text": Renders a plain text input
 *
 * switchOptions values MUST match FIELD_TYPES (PERCENTAGE=1, AMOUNT=2).
 * This aligns with FeesSection / CostsSection behavior.
 *
 * Configuration in JSON:
 * {
 *   "key": "performance",
 *   "reactComponent": "SwitchableInput",
 *   "props": {
 *     "typeField": "performancetype",
 *     "switchOptions": [
 *       { "value": 1, "icon": "FaPercentage" },
 *       { "value": 2, "icon": "FaHashtag" }
 *     ],
 *     "inputType": "dynamic",
 *     "min": 0,
 *     "max": 100
 *   }
 * }
 */
import React, { useCallback, useMemo, useRef } from 'react';
import { FaPercentage, FaMoneyBillAlt, FaHashtag } from 'react-icons/fa';
import PillSwitch from '../common/PillSwitch';
import PercentageInput from '../common/PercentageInput';
import FormattedCurrencyInput from '../common/FormattedCurrencyInput';
import { getFieldErrorStyle } from '../common/FormValidation';
import { FIELD_TYPES, getFieldType, isPercentageType } from '../../types/common/fieldTypes';
import type { FieldProps } from '../../types/dynamicForm';
import styled from 'styled-components';
import { StyledFormControl } from '../styled/CommonStyled';

interface SwitchOption {
    value: string | number;
    label?: string;
    icon?: 'FaPercentage' | 'FaMoneyBillAlt' | 'FaHashtag';
}

// Icon mapping — matches FeesSection / CostsSection toggle icons
const IconMap: Record<string, React.ReactNode> = {
    FaPercentage: <FaPercentage />,
    FaMoneyBillAlt: <FaMoneyBillAlt />,
    FaHashtag: <FaHashtag />,
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const InputRow = styled.div`
    flex: 1;
`;

const SwitchableInput: React.FC<FieldProps> = ({
    config,
    value,
    onChange,
    onBlur,
    error,
    disabled,
    formContext,
}) => {
    const props = config.props || {};
    const typeField = props.typeField as string;
    const inputType = (props.inputType as string) || 'dynamic';
    const min = (props.min as number) ?? 0;
    const max = (props.max as number) ?? 100;

    const switchOptionsJson = JSON.stringify(props.switchOptions);

    const switchOptions = useMemo<SwitchOption[]>(() => {
        const configuredOptions = props.switchOptions as SwitchOption[] | undefined;
        if (configuredOptions && configuredOptions.length > 0) {
            return configuredOptions;
        }
        return [
            { value: FIELD_TYPES.PERCENTAGE, icon: 'FaPercentage' },
            { value: FIELD_TYPES.AMOUNT, icon: 'FaMoneyBillAlt' },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [switchOptionsJson]);

    // Use getFieldType for consistency with FeesSection — handles null/0/1/2/'1'/'2'
    const rawTypeValue = formContext?.getValue(typeField);
    const currentFieldType = getFieldType(rawTypeValue);
    const isPercentage = isPercentageType(currentFieldType);

    const formContextRef = useRef(formContext);
    formContextRef.current = formContext;

    // Mirrors FeesSection.handleFieldTypeChange exactly:
    // 1. Clear inline error on value field
    // 2. Update type in local Redux state
    // 3. Save type to DB — fire and forget, DB refresh will return null value
    // NOTE: do NOT call handleChange(config.key, null) — that triggers useSaveOnBlur
    // on the value field which fires "Value cannot be empty" when the field is empty.
    // FeesSection avoids this by dispatching directly to Redux; we match that by
    // only touching the type field here.
    const handleTypeChange = useCallback((newType: string | number | boolean) => {
        if (!typeField) {
            console.warn('SwitchableInput: typeField not configured');
            return;
        }
        const ctx = formContextRef.current;
        if (!ctx) return;
        ctx.setFieldError(config.key, null);
        ctx.handleChange(typeField, newType);
        ctx.handleBlur(typeField, newType).then(() => {});
    }, [typeField, config.key]);

    const handleValueChange = useCallback((newValue: number | null) => {
        formContextRef.current?.handleChange(config.key, newValue);
    }, [config.key]);

    const handleValueBlur = useCallback((newValue: number | null) => {
        formContextRef.current?.handleBlur(config.key, newValue);
    }, [config.key]);

    // pillSwitchOptions always uses FIELD_TYPES values (same as FeesSection).
    // switchOptions from config only controls icons, not values.
    const pillSwitchOptions = useMemo(() => [
        { value: FIELD_TYPES.PERCENTAGE, label: IconMap[switchOptions[0]?.icon as string] ?? <FaPercentage /> },
        { value: FIELD_TYPES.AMOUNT,     label: IconMap[switchOptions[1]?.icon as string] ?? <FaMoneyBillAlt /> },
    ], [switchOptions]);

    const renderInput = () => {
        if (inputType === 'text') {
            return (
                <StyledFormControl
                    type="text"
                    value={(value as string) || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlur(e.target.value)}
                    style={getFieldErrorStyle(error)}
                    disabled={disabled || formContext?.isLoading(config.key)}
                    required={config.required}
                    placeholder={props.placeholder as string}
                    name={`${config.key}_input`}
                />
            );
        }

        const shouldUsePercentage = inputType === 'percentage' ||
            (inputType === 'dynamic' && isPercentage);

        if (shouldUsePercentage) {
            return (
                <PercentageInput
                    min={min}
                    max={max}
                    value={value as number | null}
                    onChange={handleValueChange}
                    onBlur={handleValueBlur}
                    style={getFieldErrorStyle(error)}
                    disabled={disabled || formContext?.isLoading(config.key)}
                    required={config.required}
                    name={`${config.key}_input`}
                />
            );
        }

        return (
            <FormattedCurrencyInput
                min={min}
                currency=""
                showSymbol={false}
                value={value as number | null}
                onChange={handleValueChange}
                onBlur={handleValueBlur}
                style={getFieldErrorStyle(error)}
                placeholder="0.00"
                disabled={disabled || formContext?.isLoading(config.key)}
                required={config.required}
                name={`${config.key}_input`}
            />
        );
    };

    return (
        <Container>
            <SwitchRow>
                <PillSwitch
                    options={pillSwitchOptions}
                    value={currentFieldType}
                    onChange={handleTypeChange}
                    disabled={disabled}
                    isLoading={false}
                    name={`${config.key}-type-switch`}
                    size="sm"
                />
            </SwitchRow>
            <InputRow>
                {renderInput()}
            </InputRow>
        </Container>
    );
};

export default SwitchableInput;
